import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { AxiosError } from 'axios'
import { PageLoader } from '../../shared/ui/Spinner'
import { EmptyState } from '../../shared/ui/EmptyState'
import { ErrorMessage } from '../../shared/ui/ErrorMessage'
import { toast } from '../../shared/ui/toastStore'
import { confirm } from '../../shared/ui/confirmStore'
import {
  SUPPLIER_PAYMENT_LABELS,
  SUPPLIER_TYPE_LABELS,
  deleteSupplier,
  getSupplierBuyers,
  getSupplierCard,
  updateSupplier,
  type SupplierBody,
} from '../../shared/api/suppliers.api'
import { RatingStars } from './RatingStars'
import { SupplierEditModal } from './SupplierEditModal'
import { SupplierComplaintsSection } from './components/SupplierComplaintsSection'
import { SupplierCompletenessPanel } from './components/SupplierCompletenessPanel'

// Страница карточки поставщика /suppliers/:id (26.07, вместо модалки):
// шапка с рейтингом-звёздами и алиасами, условия из справочника, KPI работы,
// сильные бренды/сегменты, журнал рекламаций, участие в заявках.

// Конверт ошибок API — { success: false, error: { message } } (русские тексты).
const errText = (e: unknown): string => {
  const data = (e as { response?: { data?: { error?: { message?: string }; message?: string } } })
    ?.response?.data
  return data?.error?.message ?? data?.message ?? (e as Error)?.message ?? 'Ошибка'
}

function Stat({
  label,
  value,
  accent,
}: {
  label: string
  value: number | string
  accent?: boolean
}) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white p-3">
      <div className="text-xs text-gray-500">{label}</div>
      <div
        className={`mt-1 text-lg font-semibold ${accent ? 'text-emerald-700' : 'text-gray-900'}`}
      >
        {typeof value === 'number' ? value.toLocaleString('ru-RU') : value}
      </div>
    </div>
  )
}

function TopChips({
  title,
  rows,
}: {
  title: string
  rows: Array<{ name: string; quotes: number; wins: number }>
}) {
  if (rows.length === 0) return null
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
      <p className="text-xs text-gray-500 mt-0.5">победы·квоты</p>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {rows.map((r) => (
          <span
            key={r.name}
            title={`${r.wins} в счёт · ${r.quotes} квот`}
            className={`px-2 py-0.5 rounded-lg border text-xs ${
              r.wins > 0
                ? 'bg-emerald-50/60 border-emerald-200 text-gray-800'
                : 'bg-gray-50 border-gray-200 text-gray-600'
            }`}
          >
            {r.name} <span className="text-emerald-700">{r.wins}</span>·{r.quotes}
          </span>
        ))}
      </div>
    </div>
  )
}

export default function SupplierDetailPage() {
  const { id = '' } = useParams()
  const navigate = useNavigate()
  const qc = useQueryClient()

  const [editOpen, setEditOpen] = useState(false)

  const cardQ = useQuery({
    queryKey: ['suppliers', 'card', id],
    queryFn: () => getSupplierCard(id),
    retry: (count, err) => {
      const status = (err as AxiosError).response?.status
      if (status === 403 || status === 404) return false
      return count < 2
    },
  })
  // Права на этого поставщика присылает сервер (suppliers.access.ts): РОП
  // правит только поставщиков РФ, закупщик — только пишет рекламации.
  const perms = cardQ.data?.permissions
  const canEdit = !!perms?.edit
  const canRateAndAssign = !!perms?.editRatingAndBuyer
  const buyersQ = useQuery({
    queryKey: ['suppliers', 'buyers'],
    queryFn: getSupplierBuyers,
    enabled: canRateAndAssign,
  })

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['suppliers'] })
  }

  const ratingMut = useMutation({
    mutationFn: (rating: number | null) => updateSupplier(id, { rating }),
    onSuccess: invalidate,
    onError: (e: unknown) => toast.error(errText(e)),
  })

  const handleHide = async () => {
    const s = cardQ.data?.supplier
    if (!s) return
    const ok = await confirm({
      title: `Скрыть поставщика «${s.name}»?`,
      message:
        'Квоты, заявки и вся история не пострадают — поставщик исчезнет только из справочника. Вернуть можно через режим «Скрытые».',
      confirmText: 'Скрыть',
      danger: true,
    })
    if (!ok) return
    try {
      await deleteSupplier(id)
      invalidate()
      toast.success(`«${s.name}» скрыт из справочника`)
      navigate('/suppliers')
    } catch (e) {
      toast.error(errText(e))
    }
  }

  const handleSave = async (body: SupplierBody) => {
    await updateSupplier(id, body)
    toast.success('Карточка поставщика сохранена')
    invalidate()
  }

  if (cardQ.isLoading) return <PageLoader label="Загружаю карточку поставщика…" />
  if (cardQ.isError) {
    const status = (cardQ.error as AxiosError).response?.status
    if (status === 404 || status === 403) {
      return (
        <EmptyState
          title={status === 404 ? 'Поставщик не найден' : 'Нет доступа к карточке поставщика'}
          action={
            <Link to="/suppliers" className="text-sm text-brand-600 hover:text-brand-700">
              ← К справочнику поставщиков
            </Link>
          }
        />
      )
    }
    return <ErrorMessage error={cardQ.error} />
  }
  const data = cardQ.data!
  const s = data.supplier

  return (
    <div>
      {/* Шапка */}
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <Link to="/suppliers" className="text-sm text-brand-600 hover:underline">
            ← К справочнику поставщиков
          </Link>
          <div className="mt-1 flex items-center gap-3 flex-wrap">
            <h1 className="text-2xl font-semibold text-gray-900">{s.name}</h1>
            <RatingStars
              value={s.rating}
              size="lg"
              onChange={canRateAndAssign ? (next) => ratingMut.mutate(next) : undefined}
            />
            {s.type && (
              <span className="px-2 py-0.5 rounded-lg bg-gray-100 text-gray-700 text-xs">
                {SUPPLIER_TYPE_LABELS[s.type]}
              </span>
            )}
            {s.hiddenAt && (
              <span className="px-2 py-0.5 rounded-lg bg-amber-100 text-amber-700 text-xs">
                Скрыт из справочника
              </span>
            )}
          </div>
          {s.aliases.length > 0 && (
            <div className="mt-1 text-xs text-gray-500">
              Он же: {s.aliases.map((a) => a.aliasName).join(', ')}
            </div>
          )}
          <div className="mt-1 text-sm text-gray-600">
            Ответственный:{' '}
            {s.responsibleBuyer?.name ??
              s.responsibleBuyer?.email ??
              'не назначен (видит тот, кто занёс)'}
          </div>
        </div>
        {(canEdit || perms?.hide) && (
          <div className="flex flex-wrap gap-2">
            {canEdit && (
              <button
                onClick={() => setEditOpen(true)}
                className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white hover:bg-gray-50 text-gray-700"
              >
                Редактировать
              </button>
            )}
            {perms?.hide && !s.hiddenAt && (
              <button
                onClick={() => void handleHide()}
                className="rounded-lg bg-red-100 px-3 py-1.5 text-sm font-medium text-red-700 hover:bg-red-200"
              >
                Скрыть из справочника
              </button>
            )}
          </div>
        )}
      </div>

      {/* Условия из справочника */}
      <div className="mt-4 text-xs text-gray-600 flex flex-wrap gap-x-4 gap-y-1">
        <span>Оплата: {s.paymentType ? SUPPLIER_PAYMENT_LABELS[s.paymentType] : '—'}</span>
        <span>
          Лимит:{' '}
          {s.creditLimitAmount
            ? `${Number(s.creditLimitAmount).toLocaleString('ru-RU')} ${s.creditLimitCurrency ?? ''}`
            : '—'}
        </span>
        <span>
          Отсрочка после отгрузки: {s.postShipmentDays != null ? `${s.postShipmentDays} дн.` : '—'}
        </span>
        <span>Гарантия: {s.warrantyDays != null ? `${s.warrantyDays} дн.` : '—'}</span>
        {s.paymentNotes && (
          <span className="basis-full text-gray-500">Спецусловия: {s.paymentNotes}</span>
        )}
      </div>

      {/* Полнота карточки — видно, что дозаполнить (ничего не блокирует). */}
      <SupplierCompletenessPanel
        supplier={s}
        onFill={canEdit ? () => setEditOpen(true) : undefined}
      />

      {/* Статистика работы */}
      <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Stat label="Заявок участвовал" value={data.stats.requestsInvolved} />
        <Stat label="BOM-запросов" value={data.stats.bomRequests} />
        <Stat label="Позиций на просчёт" value={data.stats.itemsRequested} />
        <Stat label="Квот дал" value={data.stats.quotesGiven} />
        <Stat label="С ценой >0" value={data.stats.quotesPriced} />
        <Stat label="Ушло в счёт" value={data.stats.wonItems} accent />
      </div>

      <div className="mt-5 space-y-6">
        <TopChips title="Сильные бренды" rows={data.topBrands} />
        <TopChips title="Сильные сегменты" rows={data.topCategories} />

        <SupplierComplaintsSection
          supplierId={id}
          supplier={s}
          canComplain={!!perms?.complain}
          onChanged={invalidate}
        />

        {/* Участие в заявках */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-sm font-semibold text-gray-900">Участие в заявках</h3>
          <p className="text-xs text-gray-500 mt-0.5">
            последние {data.recentRequests.length} из {data.stats.requestsInvolved}
          </p>
          {data.recentRequests.length === 0 ? (
            <div className="mt-3 text-xs text-gray-400">Квот по заявкам ещё не было.</div>
          ) : (
            <div className="mt-3 overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-400">
                    <th className="py-1 pr-2 font-normal">Заявка</th>
                    <th className="py-1 pr-2 font-normal">Клиент</th>
                    <th className="py-1 pr-2 font-normal">Создана</th>
                    <th className="py-1 pr-2 font-normal">Квот</th>
                    <th className="py-1 font-normal">В счёт</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recentRequests.map((r) => (
                    <tr key={r.requestId} className="border-t border-gray-50">
                      <td className="py-1 pr-2">
                        <Link
                          to={`/requests/${r.requestId}`}
                          className="text-brand-600 hover:text-brand-700 font-medium"
                        >
                          {r.requestNumber ?? r.requestId.slice(0, 8)}
                        </Link>
                      </td>
                      <td className="py-1 pr-2 text-gray-700">{r.clientName}</td>
                      <td className="py-1 pr-2 text-gray-500">
                        {new Date(r.createdAt).toLocaleDateString('ru-RU')}
                      </td>
                      <td className="py-1 pr-2">{r.quotes}</td>
                      <td className="py-1">
                        {r.wins > 0 ? (
                          <span className="text-emerald-700 font-medium">{r.wins}</span>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {editOpen && (
        <SupplierEditModal
          supplier={s}
          buyers={buyersQ.data ?? []}
          canEditBuyer={canRateAndAssign}
          onSave={handleSave}
          onClose={() => setEditOpen(false)}
        />
      )}
    </div>
  )
}
